import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getQueue, removeFromQueue, getPendingCount } from '@/utils/offlineQueue';

const API = process.env.REACT_APP_BACKEND_URL;

export function useOfflineSync() {
  const [pending, setPending] = useState(getPendingCount);

  // Aggiorna il contatore quando cambia localStorage (anche nella stessa tab)
  const refresh = useCallback(() => setPending(getPendingCount()), []);

  useEffect(() => {
    window.addEventListener('ve_queue_changed', refresh);
    return () => window.removeEventListener('ve_queue_changed', refresh);
  }, [refresh]);

  const processQueue = useCallback(async () => {
    const queue = getQueue();
    if (!queue.length) return;

    let synced = 0;
    let failed = 0;

    for (const op of queue) {
      try {
        await axios({
          method: op.method,
          url: `${API}${op.path}`,
          data: op.body,
          headers: { Authorization: `Bearer ${op.token}` },
        });
        removeFromQueue(op.id);
        synced++;
      } catch (err) {
        // 4xx = errore dati (non riprovare), 5xx = errore server (riprova dopo)
        if (err.response?.status >= 400 && err.response?.status < 500) {
          removeFromQueue(op.id);
          failed++;
        }
        // altrimenti lascia in coda per il prossimo tentativo
      }
    }

    refresh();

    if (synced > 0) {
      toast.success(`${synced} esito${synced > 1 ? 'i' : ''} sincronizzato${synced > 1 ? 'i' : ''}`);
    }
    if (failed > 0) {
      toast.error(`${failed} operazione${failed > 1 ? 'i' : ''} non riuscita${failed > 1 ? 'e' : ''} — dati non validi`);
    }
  }, [refresh]);

  // Quando torna la rete → sincronizza
  useEffect(() => {
    window.addEventListener('online', processQueue);
    return () => window.removeEventListener('online', processQueue);
  }, [processQueue]);

  // Sincronizza all'avvio se c'è rete e ci sono operazioni in coda
  useEffect(() => {
    if (navigator.onLine && getPendingCount() > 0) {
      processQueue();
    }
  }, [processQueue]);

  return { pending, refresh };
}
